import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { addDays } from '@fullcalendar/core/internal';
import { ConfigService } from 'app/services';
import { GetFinanceSettlementReportsRequest } from 'app/models/partner/get-finance-settlement-reports-request';
import {
	GetFinanceSettlementReportsResponse,
	FinanceSettlementReportDto
} from 'app/models/partner/get-finance-settlement-reports-response';
import { ExportFileExtension, ExportFileTypeEnum } from 'app/models';
import { MessageService } from 'primeng/api';
import { PartnerFinanceService } from 'app/services/partner/partner-finance.service';
import { Utils } from 'app/utils';
import moment from 'moment';

export interface SettlementTransactionData {
	storeName: string;
	transactionDate: string;
	transactionTime: string;
	transactionType: string;
	transactionAmount: number;
	promotions: number;
	netCollected: number;
	mdrFeeAmount: number;
	vatAmount: number;
	netCashable: number;
}

@Component({
	selector: 'partner-finance-settlement-reports',
	templateUrl: './partner-finance-settlement-reports.component.html',
	styleUrls: [
		'./partner-finance-settlement-reports.component.scss',
		'../../../../../styles/m3-defaul-material-theme.scss',
	],
})
export class PartnerFinanceSettlementReportsComponent implements OnInit, AfterViewInit {
	constructor(
		public configService: ConfigService,
		private fb: FormBuilder,
		private partnerFinanceService: PartnerFinanceService,
		private messageService: MessageService
	) {}

	fileTypeOptions = ExportFileTypeEnum; // Default selected file type

	displayedColumns: string[] = [
		'storeName',
		'transactionDate',
		'transactionTime',
		'transactionType',
		'transactionAmount',
		'promotions',
		'netCollected',
		'mdrFeeAmount',
		'vatAmount',
		'netCashable',
	];
	dataSource = new MatTableDataSource<SettlementTransactionData>([]);
	range!: FormGroup;
	loading = false;
	isEmpty = false;

	totalRecords = 0;
	pageSizeOptions = [5, 10, 20, 50];
    currentSortField = 'transactionDate';
    currentSortOrder = 1;
    currentSortDirection: SortDirection = 'desc';
    pageSize = 10;
	pageIndex = 0;

	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;

	ngOnInit() {
		this.setDefaultRangePicker();
	}

	ngAfterViewInit() {
	}

	setDefaultRangePicker() {
		const today = new Date();
		const last7Days = addDays(today, -6);

		this.range = this.fb.group({
			start: [last7Days],
			end: [today],
		});

		this.loadFinanceSettlementReports();
	}

	loadFinanceSettlementReports(){
		if (!this.range) {
			return;
		}

		this.loading = true;
		const skip = this.pageIndex * this.pageSize;
        const take = this.pageSize;
		const request: GetFinanceSettlementReportsRequest = {
			TimeZoneOffset: Utils.getTimezoneOffset(),
			FromDate: this.range.value.start, // Always have default value
			ToDate: this.range.value.end, // Always have default value
            Skip: skip,
            Take: take,
            SortField: this.currentSortField,
            SortOrder: this.currentSortOrder,
			HeaderColumns: [],
			DataColumns: [],
		};

		this.partnerFinanceService
			.getFinanceSettlementReports(request, this.messageService)
			.then((response: GetFinanceSettlementReportsResponse) => {
				if (response.Success && response.Items && response.Items.length > 0) {
					const mappedData = response.Items.map((item: FinanceSettlementReportDto) => ({
						storeName: item.StoreName,
						transactionDate:item.TransactionDate,
						transactionTime: item.TransactionTime,
						transactionType: item.SettlementTransactionType,
						transactionAmount: item.TransactionAmount,
						promotions: item.Promotions,
						netCollected: item.NetCollected,
						mdrFeeAmount: item.MdrFeeAmount,
						vatAmount: item.VatAmount,
						netCashable: item.NetCashable
					}));
					this.dataSource.data = mappedData;
					this.totalRecords = response.TotalRecords || 0;
					this.isEmpty = false;
				} else {
					this.dataSource.data = [];
					this.isEmpty = true;
				}
			})
			.catch((error) => {
				throw error;
			})
			.then(() => {
				if (this.dataSource.data.length === 0) {
				this.dataSource.data = [];
				this.isEmpty = true;
				}
			})
			.finally(() => {
				this.loading = false;
			});

	}

	onDateRangeChange() {
		const startControl = this.range.get('start');
		const endControl = this.range.get('end');

		const hasStartValue =
			startControl?.value !== null && startControl?.value !== undefined;
		const hasEndValue =
			endControl?.value !== null && endControl?.value !== undefined;

		if (!hasStartValue || !hasEndValue) {
			return;
		} else {
			this.pageIndex = 0;
			this.loading = true;
			this.isEmpty = false;
			this.loadFinanceSettlementReports();
		}
	}

	exportSettlementReport(fileType: ExportFileTypeEnum) {
		const request: GetFinanceSettlementReportsRequest = {
			TimeZoneOffset: Utils.getTimezoneOffset(),
			FromDate: this.range.value.start,
			ToDate: this.range.value.end,
			FileType: fileType,
			Skip: 0,
			Take: 0, // 0 means get all records for export
			SortField: this.currentSortField,
			SortOrder: this.currentSortOrder,
			HeaderColumns: [],
			DataColumns: [],
		};

		this.partnerFinanceService.exportFinanceSettlementReports(request, this.messageService)
		.then(data => {
			this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Success' });
			Utils.saveFileResponse(data, 'FinanceSettlementReport' +
			moment(new Date()).format('DD MMM YYYY hh:mm:ss A') + ExportFileExtension[ExportFileTypeEnum[fileType] as keyof typeof ExportFileExtension]);
			this.loading = false;
			})
			.catch(err => {
				this.loading = false;
				throw err;
			});
	}

	// Pagination event handlers
	onPageChange(event: PageEvent) {
		this.pageIndex = event.pageIndex;
		this.pageSize = event.pageSize;
		this.loadFinanceSettlementReports();
	}

	// Sorting event handler
	onSortChange(event: Sort) {
		this.currentSortField = event.active;
		this.currentSortOrder = this.currentSortDirection === 'asc' ? 1 : -1;
		this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
		this.pageIndex = 0; // Reset to first page when sorting changes
		this.loadFinanceSettlementReports();
	}

}
